import { memo, useCallback, useEffect, useState } from 'react';

import { displayDuration, getDuration } from 'utils';
import { Card, MembersOnlyBadge } from 'components';
import { ArrowRightIcon, CameraIcon, LiveWaveIcon, PlayCircleNewIcon } from 'components/Icon';

import styles from './EventCard.Ongoing.module.scss';

export type OngoingEventCardProps = {
  poster?: string;
  eventCode: string;
  eventName: string;
  startDate: string;
  channelName: string;
  isMembersOnly?: boolean;
  hasAccess?: boolean;
  onClick?(): void;
};

const Duration = memo<{ startDate: string }>(({ startDate }) => {
  const [duration, setDuration] = useState(() => getDuration(startDate));

  useEffect(() => {
    const durationInterval = setInterval(() => {
      setDuration(() => getDuration(startDate));
    }, 1_000);

    return () => {
      clearInterval(durationInterval);
    };
  }, [startDate]);

  return <span className={styles.duration}>{displayDuration(duration)}</span>;
});

const OngoingEventCard = ({
  eventCode,
  eventName,
  startDate,
  poster,
  channelName,
  isMembersOnly = false,
  hasAccess = false,
  onClick
}: OngoingEventCardProps): JSX.Element => {
  const [posterLoadFailed, setPosterLoadFailed] = useState(!poster);

  const handleOnPosterLoadFailed = useCallback(() => setPosterLoadFailed(true), []);

  const handleOnClick = () => {
    onClick?.();
  };

  return (
    <Card className={styles.container} onClick={handleOnClick}>
      <div className={styles.poster}>
        <div className={styles.live}>
          <LiveWaveIcon className={styles.wave} />
          <Duration startDate={startDate} />
        </div>
        <PlayCircleNewIcon className={styles.play} />
        {posterLoadFailed || !poster ? (
          <div className={styles.placeholder}>
            <CameraIcon />
          </div>
        ) : (
          <img className={styles.image} src={poster} alt={eventName} onError={handleOnPosterLoadFailed} />
        )}
        {isMembersOnly && <MembersOnlyBadge className={styles.membersOnlyBadge} hasAccess={hasAccess} />}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{eventName}</div>
        <div className={styles.channel}>@{channelName}</div>
        <div className={styles.cta}>
          <span className="mr-4">Join now</span>
          <ArrowRightIcon />
        </div>
      </div>
    </Card>
  );
};

export default memo(OngoingEventCard);
